import { Button, Container } from '@playcanvas/pcui';

import { NumericInput } from '../ui/numeric-input';
import { TranslateGizmo, Vec3 } from 'playcanvas';

import { BoxShape } from '../box-shape';
import { Events } from '../events';
import { Scene } from '../scene';
import { Splat } from '../splat';
import { localize } from '../ui/localization';

class BoxSelection {
    activate: () => void;
    deactivate: () => void;

    active = false;

    constructor(events: Events, scene: Scene, canvasContainer: Container) {
        const box = new BoxShape();

        const gizmo = new TranslateGizmo(scene.camera.camera, scene.gizmoLayer);

        gizmo.on('render:update', () => {
            scene.forceRender = true;
        });

        gizmo.on('transform:move', () => {
            box.moved();
        });

        // ui
        const selectToolbar = new Container({
            class: 'select-toolbar',
            hidden: true
        });

        selectToolbar.dom.addEventListener('pointerdown', (e) => {
            e.stopPropagation();
        });

        const selectButton = new Button({ text: localize('toolbar.select'), class: 'select-toolbar-button' });
        const invertButton = new Button({ text: localize('toolbar.invert'), class: 'select-toolbar-button' });
        const deleteButton = new Button({
            text: localize('menu.select.delete'),
            // PCUI passes `class` to classList.add (one token only).
            class: 'select-toolbar-button--danger'
        });

        const dimInputWidth = 88;

        const lenX = new NumericInput({
            precision: 2,
            value: box.lenX,
            width: dimInputWidth,
            min: 0.01
        });

        const lenY = new NumericInput({
            precision: 2,
            value: box.lenY,
            width: dimInputWidth,
            min: 0.01
        });

        const lenZ = new NumericInput({
            precision: 2,
            value: box.lenZ,
            width: dimInputWidth,
            min: 0.01
        });

        selectToolbar.append(selectButton);
        selectToolbar.append(invertButton);
        selectToolbar.append(deleteButton);

        const makeWrap = (input: NumericInput, label: string) => {
            const wrap = document.createElement('span');
            wrap.className = 'select-toolbar-numeric-wrap';
            wrap.appendChild(input.dom);
            const suffix = document.createElement('span');
            suffix.className = 'select-toolbar-unit';
            suffix.textContent = label;
            wrap.appendChild(suffix);
            return wrap;
        };

        selectToolbar.dom.appendChild(makeWrap(lenX, 'X'));
        selectToolbar.dom.appendChild(makeWrap(lenY, 'Y'));
        selectToolbar.dom.appendChild(makeWrap(lenZ, 'Z'));

        canvasContainer.append(selectToolbar);

        const applyBox = (op: 'add' | 'remove' | 'set') => {
            const p = box.pivot.getPosition();
            events.fire('select.byBox', op, [p.x, p.y, p.z, box.lenX, box.lenY, box.lenZ]);
        };

        selectButton.dom.addEventListener('pointerdown', (e) => {
            e.stopPropagation();
            applyBox('add');
        });
        invertButton.dom.addEventListener('pointerdown', (e) => {
            e.stopPropagation();
            events.fire('select.invert');
        });
        deleteButton.dom.addEventListener('pointerdown', (e) => {
            e.stopPropagation();
            events.fire('select.delete');
        });
        lenX.on('change', () => {
            box.lenX = lenX.value;
        });
        lenY.on('change', () => {
            box.lenY = lenY.value;
        });
        lenZ.on('change', () => {
            box.lenZ = lenZ.value;
        });

        events.on('camera.focalPointPicked', (details: { splat: Splat, position: Vec3 }) => {
            if (this.active) {
                box.pivot.setPosition(details.position);
                gizmo.attach([box.pivot]);
            }
        });

        const updateGizmoSize = () => {
            const { camera, canvas } = scene;
            if (camera.ortho) {
                gizmo.size = 1125 / canvas.clientHeight;
            } else {
                gizmo.size = 1200 / Math.max(canvas.clientWidth, canvas.clientHeight);
            }
        };
        updateGizmoSize();
        events.on('camera.resize', updateGizmoSize);
        events.on('camera.ortho', updateGizmoSize);

        this.activate = () => {
            this.active = true;
            scene.add(box);
            gizmo.attach([box.pivot]);
            selectToolbar.hidden = false;
        };

        this.deactivate = () => {
            selectToolbar.hidden = true;
            gizmo.detach();
            scene.remove(box);
            this.active = false;
        };
    }
}

export { BoxSelection };
